import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PropTypes from 'prop-types';
import { FiPlus, FiX } from 'react-icons/fi';
import { RxDragHandleDots2 } from 'react-icons/rx';

import { PRIORITY_ROLE_OPTIONS, getGermanLabel } from '#lib/constants/contactRoles';

function SortableRoleRow({ roleKey, rank, removable, onRemove }) {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
		id: roleKey,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.4 : undefined,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1"
		>
			<button
				type="button"
				ref={setActivatorNodeRef}
				className="cursor-grab touch-none text-bgBase-800 hover:text-text"
				title="Zum Verschieben ziehen"
				{...attributes}
				{...listeners}
			>
				<RxDragHandleDots2 size={14} />
			</button>
			<span className="w-5 text-xs font-semibold text-primary-600">{rank}.</span>
			<span className="flex-1 truncate text-sm text-text">{getGermanLabel(roleKey)}</span>
			<button
				type="button"
				onClick={onRemove}
				disabled={!removable}
				className="rounded p-0.5 text-bgBase-800 hover:text-warning-500 disabled:cursor-not-allowed disabled:opacity-30"
				title={removable ? 'Rolle entfernen' : 'Mindestens ein Eintrag erforderlich'}
			>
				<FiX size={14} />
			</button>
		</div>
	);
}

SortableRoleRow.propTypes = {
	roleKey: PropTypes.string.isRequired,
	rank: PropTypes.number.isRequired,
	removable: PropTypes.bool.isRequired,
	onRemove: PropTypes.func.isRequired,
};

export default function RolePriorityPicker({ value, onChange, options = PRIORITY_ROLE_OPTIONS }) {
	// Small drag threshold so plain clicks still hit the row buttons instead of starting a drag.
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

	const unused = options.filter((r) => !value.includes(r.key));
	const companyIndex = value.indexOf('company');
	const hasDeadEntries = companyIndex !== -1 && companyIndex < value.length - 1;

	const handleDragEnd = ({ active, over }) => {
		if (!over || active.id === over.id) return;
		const from = value.indexOf(active.id);
		const to = value.indexOf(over.id);
		if (from === -1 || to === -1) return;
		onChange(arrayMove(value, from, to));
	};

	return (
		<div className="space-y-3">
			<div>
				<p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-bgBase-800">
					Priorität (ziehen zum Umsortieren)
				</p>
				<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
					<SortableContext items={value} strategy={verticalListSortingStrategy}>
						{/* Bounded height keeps the surrounding modal compact; dnd-kit auto-scrolls while dragging. */}
						<div className="max-h-52 space-y-1 overflow-y-auto pr-1">
							{value.map((roleKey, index) => (
								<SortableRoleRow
									key={roleKey}
									roleKey={roleKey}
									rank={index + 1}
									removable={value.length > 1}
									onRemove={() => onChange(value.filter((key) => key !== roleKey))}
								/>
							))}
						</div>
					</SortableContext>
				</DndContext>
				{hasDeadEntries && (
					<p className="mt-1.5 text-xs text-caution-700">
						Rollen nach „Firmenkontakt“ kommen nie zum Zug, da die Firma selbst immer als Empfänger
						verfügbar ist.
					</p>
				)}
			</div>
			{unused.length > 0 && (
				<div>
					<p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-bgBase-800">
						Nicht verwendet
					</p>
					<div className="flex flex-wrap gap-1.5">
						{unused.map((role) => (
							<button
								key={role.key}
								type="button"
								onClick={() => onChange([...value, role.key])}
								className="flex items-center gap-1 rounded-full border border-dashed border-gray-300 bg-gray-50 px-2.5 py-1 text-xs text-bgBase-800 hover:border-purple-400 hover:text-primary-700"
							>
								<FiPlus size={12} />
								{role.label}
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

RolePriorityPicker.propTypes = {
	value: PropTypes.arrayOf(PropTypes.string).isRequired,
	onChange: PropTypes.func.isRequired,
	options: PropTypes.arrayOf(
		PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
	),
};
